import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const apprenantService = {
  getAll: (zone = null) => api.get('/bénéficiaires', { params: { zone } }),
  getById: (id) => api.get(`/bénéficiaires/${id}`),
  create: (data) => api.post('/bénéficiaires', data),
  update: (id, data) => api.put(`/bénéficiaires/${id}`, data),
  // Statut de réception : { apprenant_id: { recu, date_premiere_signature } }
  getReceptionStatus: () => api.get('/apprenants/statut-reception')
};

// ── journalAccesService — Journal des accès aux dossiers (admin uniquement) ──
// Contrepartie de l'élargissement des habilitations : qui a consulté quel
// dossier et quand. En lecture seule : aucune route ne modifie le journal.
export const journalAccesService = {
  list: (params = {}) => api.get('/journal-acces', { params }),
  parBeneficiaire: (apprenantId) => api.get(`/journal-acces/beneficiaire/${apprenantId}`),
  resume: (jours = 30) => api.get('/journal-acces/resume', { params: { jours } })
};

export const consentementService = {
  // Liste des consentements répondus (accepte/refuse), filtre optionnel par statut
  list: (statut = null) => api.get('/consentements', { params: { statut } }),
  // PDF du formulaire signé d'un bénéficiaire
  downloadIndividuel: (apprenantId) =>
    api.get(`/reporting/consentement/${apprenantId}/pdf`, { responseType: 'blob' }),
  // PDF regroupant tous les consentements signés
  downloadCollectif: (statut = null) =>
    api.get('/reporting/consentements/pdf', { params: { statut }, responseType: 'blob' })
};

export const entrepriseService = {
  getAll: () => api.get('/entreprises'),
  create: (data) => api.post('/entreprises', data)
};

export const actionService = {
  getAll: (bénéficiaireId = null) => api.get('/actions', { params: { apprenant_id: bénéficiaireId } }),
  create: (data) => api.post('/actions', data),
  update: (id, data) => api.put(`/actions/${id}`, data),
  delete: (id) => api.delete(`/actions/${id}`),
  getStats: (bénéficiaireId) => api.get(`/actions/stats/${bénéficiaireId}`),
  signIntervenant: (id, signature) => api.put(`/actions/${id}/signature-intervenant`, { signature }),
  signApprenant: (id, signature) => api.put(`/actions/${id}/signature-apprenant`, { signature }),
  updatePresence: (id, statut) => api.put(`/actions/${id}/presence`, null, { params: { statut } }),
  // Gestion des invités
  addInvites: (id, invitesIds) => api.post(`/actions/${id}/invites`, invitesIds),
  updateInvitePresence: (actionId, inviteId, present) => api.put(`/actions/${actionId}/invite/${inviteId}/presence`, null, { params: { present } }),
  signInvite: (actionId, inviteId, signature) => api.put(`/actions/${actionId}/invite/${inviteId}/signature`, { signature })
};

export const atelierService = {
  getAll: (zone = null) => api.get('/ateliers', { params: { zone } }),
  create: (data) => api.post('/ateliers', data),
  update: (id, data) => api.put(`/ateliers/${id}`, data),
  delete: (id) => api.delete(`/ateliers/${id}`),
  updateParticipants: (id, participants) => api.put(`/ateliers/${id}/participants`, participants),
  addParticipants: (id, participantsIds) => api.post(`/ateliers/${id}/add-participants`, participantsIds),
  // Signature animateur : passe par PUT /ateliers/{id}/emargement (EmargementDialog)
  // Gestion des invités
  addInvites: (id, invitesIds) => api.post(`/ateliers/${id}/invites`, invitesIds),
  removeInvite: (atelierId, inviteId) => api.delete(`/ateliers/${atelierId}/invite/${inviteId}`),
  updateInvitePresence: (atelierId, inviteId, present) => api.put(`/ateliers/${atelierId}/invite/${inviteId}/presence`, null, { params: { present } }),
  signInvite: (atelierId, inviteId, signature) => api.put(`/ateliers/${atelierId}/invite/${inviteId}/signature`, { signature })
};

export const visiteService = {
  getAll: (contratId = null) => api.get('/visites', { params: { contrat_id: contratId } }),
  create: (data) => api.post('/visites', data)
};

export const contratService = {
  getAll: (apprenantId = null) => api.get('/contrats', { params: { apprenant_id: apprenantId } }),
  create: (data) => api.post('/contrats', data),
  update: (id, data) => api.put(`/contrats/${id}`, data),
  delete: (id) => api.delete(`/contrats/${id}`),
  // Téléchargement
  download: (id) => api.get(`/contrats/${id}/document`, { responseType: 'blob' }),
  downloadMultiple: (ids) => api.post('/contrats/download-multiple', ids, { responseType: 'blob' }),
  listDownloadable: (apprenantId = null) => api.get('/contrats/list-downloadable', { params: { apprenant_id: apprenantId } })
};

export const statsService = {
  getGlobal: (zone = null) => api.get('/stats/global', { params: { zone } })
};

export const reportingService = {
  getSyntheseIndividuelle: (bénéficiaireId, mois, annee) => {
    return api.get(`/reporting/synthese-individuelle/${bénéficiaireId}/${mois}/${annee}`, {
      responseType: 'blob'
    });
  }
};

export const userService = {
  getAll: () => api.get('/users'),
  getAnimateurs: () => api.get('/animateurs')
};

// Service pour les comptes-rendus de RDV (suivi des bénéficiaires)
export const compteRenduService = {
  list: (beneficiaireId = null) =>
    api.get('/comptes-rendus', { params: beneficiaireId ? { beneficiaire_id: beneficiaireId } : {} }),
  get: (id) => api.get(`/comptes-rendus/${id}`),
  create: (data) => api.post('/comptes-rendus', data),
  update: (id, data) => api.put(`/comptes-rendus/${id}`, data),
  delete: (id) => api.delete(`/comptes-rendus/${id}`),
  dashboard: () => api.get('/comptes-rendus/suivi/dashboard')
};

export const permissionService = {
  getAvailable: () => api.get('/permissions/available'),
  getAllRoles: () => api.get('/permissions/roles'),
  getRolePermissions: (role) => api.get(`/permissions/role/${role}`),
  updateRolePermissions: (role, permissions) => api.put(`/permissions/role/${role}`, permissions),
  resetRolePermissions: (role) => api.post(`/permissions/reset/${role}`),
  getMyPermissions: () => api.get('/permissions/my')
};

// Service pour les projets et dossiers
export const projetService = {
  getAll: () => api.get('/projets'),
  create: (data) => api.post('/projets', data),
  update: (id, data) => api.put(`/projets/${id}`, data),
  delete: (id) => api.delete(`/projets/${id}`),
  // Dossiers
  getDossiers: (projetId) => api.get(`/projets/${projetId}/dossiers`),
  createDossier: (projetId, data) => api.post(`/projets/${projetId}/dossiers`, data),
  updateDossier: (projetId, dossierId, data) => api.put(`/projets/${projetId}/dossiers/${dossierId}`, data),
  deleteDossier: (projetId, dossierId) => api.delete(`/projets/${projetId}/dossiers/${dossierId}`)
};

// ── emailComposeService — Envoi d'emails depuis l'interface ─────────────────
export const emailComposeService = {
  async sendEmail({ toEmail, subject, bodyHtml, bodyText = null, attachments = [] }) {
    const formData = new FormData();
    formData.append('to_email', toEmail);
    formData.append('subject', subject);
    formData.append('body_html', bodyHtml);
    if (bodyText) formData.append('body_text', bodyText);
    attachments.forEach((file) => formData.append('attachments', file));
    const { data } = await api.post('/email/send', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async sendBulkEmail({ recipients, subject, bodyHtml }) {
    const { data } = await api.post('/email/send-bulk', {
      recipients,
      subject,
      body_html: bodyHtml,
    });
    return data;
  },

  async getAnnuaire() {
    const { data } = await api.get('/email/annuaire');
    return data;
  },
};

// ── authService — Gestion des comptes utilisateurs ───────────────────────────
export const authService = {
  async resendSetupLink(userId) {
    const { data } = await api.post(`/auth/resend-setup-link/${userId}`, {});
    return data;
  },
};

// ── diagnosticService — Diagnostics de compétences ──────────────────────────
// La saisie est ouverte aux intervenants (animateur), référents et admins :
//   - getSuivi()            : état des diagnostics de tous les bénéficiaires visibles
//   - saisirPourApprenant() : création/correction par l'intervenant (traçée)
//   - valider()             : validation d'un diagnostic auto-évalué par le bénéficiaire
export const diagnosticService = {
  getOptions: () => api.get('/diagnostic-competences/options'),
  getSuivi: () => api.get('/diagnostic-competences/suivi'),
  getByApprenant: (apprenantId) => api.get(`/diagnostic-competences/apprenant/${apprenantId}`),
  saisirPourApprenant: (apprenantId, data) =>
    api.post(`/diagnostic-competences/apprenant/${apprenantId}`, data),
  // Pré-remplissage à partir des comptes-rendus : toujours enregistré en brouillon,
  // il doit être vérifié et validé par un intervenant pour faire foi.
  proposerPourApprenant: (apprenantId, data) =>
    api.post(`/diagnostic-competences/apprenant/${apprenantId}`, {
      ...data,
      statut: 'en_cours',
      origine: 'proposition_automatique'
    }),
  valider: (diagnosticId, data) =>
    api.put(`/diagnostic-competences/${diagnosticId}/valider`, data)
};

export default api;
